import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import type { components } from '@/api/generated/schema';
import { Permissions } from '@/auth/permissions';
import { useHasPermission } from '@/auth/RequirePermission';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  InvoiceInvalidError,
  InvoiceRefusedError,
  useCreateJobInvoice,
} from '@/routes/invoices/useInvoices';

type JobInvoiceRow = components['schemas']['JobInvoiceResponse'];

/**
 * The job's invoice (M2-10; Kyle, 2026-10-02): on a Complete job, "Create
 * invoice" — for the office AND the technician on the job, who raises it on
 * site — and once raised, which invoice it is.
 *
 * <b>Who sees Create invoice.</b> Everyone who can open the job: the API lets
 * the office (`customers.manage`) raise it on any job and a technician on a
 * job they are on, and a technician can only open jobs they are on. So the
 * page offers it to whoever is looking, and only on a Complete job with no
 * live invoice — the screen never offers what the API is bound to refuse.
 *
 * <b>Where it goes afterwards.</b> Someone who can open invoices
 * (`customers.read`, the office) lands on the draft to check and send it. A
 * technician cannot open invoice screens (M2-12 made them the office's), so
 * they stay on the job, which now names the draft and says the office sends
 * it. A 200 `AlreadyInvoiced` (a double tap, or the office got there first)
 * is the same success: it names the same draft.
 *
 * <b>What the job shows.</b> The live invoice's number and status — a link for
 * the office, plain text for a technician — and any voided ones beneath it.
 * No money: the job's response carries none, on purpose.
 */
export function JobInvoice({
  jobId,
  status,
  customerName,
  lineCount,
  invoices,
  onAnnounce,
}: {
  jobId: string;
  status: string;
  customerName: string;
  lineCount: number;
  invoices: JobInvoiceRow[];
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useId();
  const hasPermission = useHasPermission();
  const canOpenInvoices = hasPermission(Permissions.readCustomers);
  const create = useCreateJobInvoice(jobId);
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const live = invoices.find((i) => i.status !== 'Void');
  const voided = invoices.filter((i) => i.status === 'Void');
  // Complete, or Invoiced with no live invoice (Kyle, 2026-10-02, review Q1:
  // a job marked Invoiced before M2-10, or seeded so, was otherwise a dead end).
  const canCreate = (status === 'Complete' || status === 'Invoiced') && !live;

  if (!canCreate && invoices.length === 0) {
    return null;
  }

  function confirm() {
    setProblem(null);
    create.mutate(undefined, {
      onSuccess: (created) => {
        if (canOpenInvoices) {
          navigate(`/invoices/${created.invoiceId}`);
          return;
        }
        setConfirming(false);
        onAnnounce(t('jobDetail.invoice.created', { number: Number(created.number) }));
      },
      onError: (error) =>
        setProblem(
          error instanceof InvoiceRefusedError || error instanceof InvoiceInvalidError
            ? t('jobDetail.invoice.refused', { reason: error.message })
            : t('jobDetail.invoice.failed'),
        ),
    });
  }

  const label = (row: JobInvoiceRow) =>
    t('jobDetail.invoice.numbered', {
      number: Number(row.number),
      status: t(`invoices.status.${row.status}`, { defaultValue: row.status }),
    });

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
      data-testid="job-invoice"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('jobDetail.invoice.heading')}
      </h2>

      {live ? (
        <p className="text-sm" data-testid="job-invoice-live">
          {canOpenInvoices ? (
            <Link to={`/invoices/${live.id}`} className="font-medium hover:underline">
              {label(live)}
            </Link>
          ) : live.status === 'Draft' ? (
            // M2-10 (Kyle, 2026-10-02): the technician opens the DRAFT they
            // raised, on its lines-only page; a sent one is the office's.
            <Link to={`/invoices/${live.id}/lines`} className="font-medium hover:underline">
              {label(live)}
            </Link>
          ) : (
            <span className="font-medium">{label(live)}</span>
          )}
          {!canOpenInvoices && live.status === 'Draft' ? (
            <span className="block text-[var(--color-muted-foreground)]">
              {t('jobDetail.invoice.officeSends')}
            </span>
          ) : null}
        </p>
      ) : null}

      {canCreate ? (
        <div className="space-y-2">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {lineCount === 0
              ? t('jobDetail.invoice.createHelpEmpty', { customer: customerName })
              : t('jobDetail.invoice.createHelp', { count: lineCount, customer: customerName })}
          </p>
          <Button
            type="button"
            className="w-full sm:w-auto"
            disabled={create.isPending}
            onClick={() => {
              setProblem(null);
              setConfirming(true);
            }}
          >
            {t('jobDetail.invoice.create')}
          </Button>
        </div>
      ) : null}

      {voided.length > 0 ? (
        <ul className="space-y-1 text-sm text-[var(--color-muted-foreground)]" data-testid="job-invoice-voided">
          {voided.map((row) => (
            <li key={row.id}>
              {canOpenInvoices ? (
                <Link to={`/invoices/${row.id}`} className="hover:underline">
                  {label(row)}
                </Link>
              ) : (
                label(row)
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          titleId={`${id}-confirm-title`}
          title={t('jobDetail.invoice.confirmTitle')}
          description={
            lineCount === 0
              ? t('jobDetail.invoice.confirmDescriptionEmpty')
              : t('jobDetail.invoice.confirmDescription', { count: lineCount })
          }
          confirmLabel={
            create.isPending ? t('jobDetail.invoice.creating') : t('jobDetail.invoice.confirm')
          }
          destructive={false}
          isPending={create.isPending}
          errorMessage={problem ?? undefined}
          onConfirm={confirm}
          onClose={() => setConfirming(false)}
          testId="create-invoice-dialog"
        />
      ) : null}
    </section>
  );
}
